import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { label: "Dekorasyon", href: "/dekorasyon" },
  { label: "Projeler", href: "/projeler" },
  { label: "Blog", href: "/blog" },
  { label: "Referanslar", href: "/referanslar" },
  { label: "Hakkımızda", href: "/hakkimizda" },
  { label: "İletişim", href: "/iletisim" },
];

const SERVICES_DROPDOWN = [
  { label: "Reklam Tabela", slug: "reklam-tabela", icon: "ri-advertisement-line", desc: "Işıklı/ışıksız tabela, yönlendirme ve özel tasarım çözümleri" },
  { label: "Işıklı Tabela", slug: "isikli-tabela", icon: "ri-lightbulb-line", desc: "Yüksek görünürlüklü LED ışıklı tabela uygulamaları" },
  { label: "Kutu Harf Işıklı Tabela", slug: "kutu-harf-tabela", icon: "ri-text-spacing", desc: "Paslanmaz ve akrilik kutu harf çözümleri" },
  { label: "Işıksız Tabela", slug: "isiksiz-tabela", icon: "ri-contrast-drop-line", desc: "Kurumsal, sade ve dayanıklı dış mekan tabela üretimi" },
  { label: "Cephe Kaplama", slug: "cephe-kaplama", icon: "ri-building-4-line", desc: "Alüminyum kompozit & modern kaplama sistemleri" },
  { label: "Işıklı Menü Board", slug: "isikli-menu-board", icon: "ri-lightbulb-flash-line", desc: "Cafe/restoran menü board tasarım ve üretimi" },
  { label: "Kutu Harf Tabela", slug: "kutu-harf-tabela", icon: "ri-text-spacing", desc: "LED ışıklı, paslanmaz çelik harf tabelalar" },
  { label: "Totem Tabela", slug: "totem-tabela", icon: "ri-layout-column-line", desc: "AVM & işyeri önü dikkat çekici totemler" },
  { label: "Yol & Yönlendirme Tabelası", slug: "yol-yonlendirme-tabelasi", icon: "ri-signpost-line", desc: "Kurum ve tesisler için iç-dış yönlendirme çözümleri" },
  { label: "Cafe & Restorant Dekorasyon", slug: "cafe-restorant-dekorasyon", icon: "ri-store-2-line", desc: "Konsept tasarım ve iç mekan uygulamaları" },
  { label: "Grafik Tasarım", slug: "grafik-tasarim", icon: "ri-palette-line", desc: "Logo, kurumsal kimlik ve sosyal medya tasarımları" },
  { label: "Anahtar Teslim Proje", slug: "anahtar-teslim-proje", icon: "ri-key-2-line", desc: "Tasarımdan montaja tam entegre çözümler" },
  { label: "LED Teknolojisi", slug: "led-teknolojisi", icon: "ri-lightbulb-flash-line", desc: "Enerji verimli, dikkat çekici LED sistemleri" },
  { label: "Matbaa / Baskı", slug: "matbaa-baski", icon: "ri-printer-line", desc: "Geniş format baskı, banner ve sticker üretimi" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActiveLink = (href: string) => {
    if (href === "/blog") return location.pathname === "/blog" || location.pathname.startsWith("/blog/");
    return location.pathname === href;
  };
  const forceLightHeader = location.pathname.startsWith("/blog/");
  const headerSolid = scrolled || forceLightHeader;

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          headerSolid ? "bg-white border-b border-[#E8E8E8]" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-[70px]">
            {/* Logo */}
            <Link to="/" className="flex items-center cursor-pointer shrink-0">
              <img
                src="/images/127d7399-8f08-4ae5-b4d1-34aa8a0fa5a8_cropped-VIP_LOGO-removebg-preview-95b748ba.webp"
                alt="VİP Reklam Dekorasyon"
                className="h-10 w-auto object-contain drop-shadow-md"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-0">
              <Link
                to="/"
                className={`px-4 py-2 font-head font-700 uppercase text-xs tracking-wider whitespace-nowrap cursor-pointer transition-colors ${
                  location.pathname === "/"
                    ? "text-[#E6A800]"
                    : headerSolid
                    ? "text-[#0D0D0D] hover:text-[#E6A800]"
                    : "text-white hover:text-[#E6A800]"
                }`}
              >
                Ana Sayfa
              </Link>

              {/* Hizmetler Mega Menu */}
              <div
                className="relative"
                ref={dropdownRef}
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 px-4 py-2 font-head font-700 uppercase text-xs tracking-wider whitespace-nowrap cursor-pointer transition-colors ${
                    servicesOpen
                      ? "text-[#E6A800]"
                      : headerSolid
                      ? "text-[#0D0D0D] hover:text-[#E6A800]"
                      : "text-white hover:text-[#E6A800]"
                  }`}
                >
                  Hizmetler
                  <i
                    className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${
                      servicesOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Mega Dropdown */}
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-0 transition-all duration-300 origin-top z-50 ${
                    servicesOpen
                      ? "opacity-100 scale-y-100 pointer-events-auto"
                      : "opacity-0 scale-y-95 pointer-events-none"
                  }`}
                  style={{ width: "860px" }}
                >
                  {/* Top connector line */}
                  <div className="h-[3px] bg-[#E6A800]" />
                  <div className="bg-white flex">
                    {/* Services Grid */}
                    <div className="flex-1 p-5 grid grid-cols-2 gap-1 max-h-[430px] overflow-y-auto">
                      {SERVICES_DROPDOWN.map((svc) => (
                        <Link
                          key={svc.label}
                          to={`/hizmetler/${svc.slug}`}
                          className="flex items-start gap-3 p-3 group hover:bg-[#FFF8E1] transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center shrink-0 bg-[#F5F5F5] group-hover:bg-[#E6A800] transition-colors mt-0.5">
                            <i className={`${svc.icon} text-sm text-[#737373] group-hover:text-white transition-colors`} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-head font-700 uppercase text-[11px] tracking-wide text-[#0D0D0D] group-hover:text-[#E6A800] transition-colors whitespace-nowrap">
                              {svc.label}
                            </div>
                            <div className="text-[#9E9E9E] text-[10px] leading-tight mt-0.5 hidden lg:block">
                              {svc.desc}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {/* Right CTA Panel */}
                    <div className="w-[195px] shrink-0 bg-[#0D0D0D] p-6 flex flex-col justify-between">
                      <div>
                        <div className="w-6 h-[2px] bg-[#E6A800] mb-4" />
                        <p className="font-head font-black uppercase text-white text-sm leading-tight mb-2">
                          Ücretsiz<br />
                          <span className="text-[#E6A800]">Keşif & Teklif</span>
                        </p>
                        <p className="text-white/40 text-[10px] leading-relaxed mt-3">
                          Osmaniye ve çevresinde ücretsiz ölçüm ve danışmanlık yapıyoruz.
                        </p>
                      </div>
                      <div className="space-y-2 mt-5">
                        <a
                          href="tel:+905511830918"
                          className="flex items-center gap-2 px-3 py-2.5 bg-[#E6A800] text-white font-head font-700 uppercase text-[10px] tracking-wider hover:bg-[#C99500] transition-colors whitespace-nowrap"
                        >
                          <i className="ri-phone-line text-xs" />
                          Hemen Ara
                        </a>
                        <a
                          href="https://wa.me/905511830918"
                          target="_blank"
                          rel="nofollow noreferrer"
                          className="flex items-center gap-2 px-3 py-2.5 bg-[#25D366]/15 border border-[#25D366]/30 text-[#25D366] font-head font-700 uppercase text-[10px] tracking-wider hover:bg-[#25D366]/25 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-whatsapp-line text-xs" />
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Bottom accent */}
                  <div className="h-[2px] bg-[#F0F0F0]" />
                </div>
              </div>

              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 font-head font-700 uppercase text-xs tracking-wider whitespace-nowrap cursor-pointer transition-colors ${
                    isActiveLink(link.href)
                      ? "text-[#E6A800]"
                      : headerSolid
                      ? "text-[#0D0D0D] hover:text-[#E6A800]"
                      : "text-white hover:text-[#E6A800]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA + Mobile toggle */}
            <div className="flex items-center gap-3">
              <a
                href="tel:+905511830918"
                className="hidden lg:flex items-center gap-2 px-5 py-2.5 font-head font-700 uppercase text-xs tracking-wider whitespace-nowrap cursor-pointer transition-all bg-[#E6A800] text-white hover:bg-[#C99500]"
              >
                <i className="ri-phone-line text-sm" />
                +90 551 183 09 18
              </a>
              <button
                className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${
                  headerSolid ? "text-[#0D0D0D]" : "text-white"
                }`}
                onClick={() => setMobileOpen((p) => !p)}
              >
                <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-[#E8E8E8] py-4 px-6">
            <Link
              to="/"
              className="block py-3 font-head font-700 uppercase text-sm tracking-wider text-[#0D0D0D] hover:text-[#E6A800] border-b border-[#F0F0F0] tr"
            >
              Ana Sayfa
            </Link>

            {/* Mobile Hizmetler accordion */}
            <button
              className="w-full flex items-center justify-between py-3 font-head font-700 uppercase text-sm tracking-wider text-[#0D0D0D] hover:text-[#E6A800] border-b border-[#F0F0F0] tr cursor-pointer"
              onClick={() => setMobileServicesOpen((p) => !p)}
            >
              Hizmetler
              <i className={`ri-arrow-down-s-line text-sm tr ${mobileServicesOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileServicesOpen && (
              <div className="bg-[#FAFAFA] border-b border-[#F0F0F0]">
                {SERVICES_DROPDOWN.map((svc) => (
                  <Link
                    key={svc.label}
                    to={`/hizmetler/${svc.slug}`}
                    className="flex items-center gap-2.5 px-4 py-2.5 font-head font-600 text-xs tracking-wider text-[#555] hover:text-[#E6A800] tr border-b border-[#F0F0F0] last:border-0"
                  >
                    <i className={`${svc.icon} text-[#E6A800] text-sm`} />
                    {svc.label}
                  </Link>
                ))}
              </div>
            )}
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="block py-3 font-head font-700 uppercase text-sm tracking-wider text-[#0D0D0D] hover:text-[#E6A800] border-b border-[#F0F0F0] tr"
              >
                {link.label}
              </Link>
            ))}
            <a
              href="tel:+905511830918"
              className="mt-4 flex items-center gap-2 px-5 py-3 bg-[#E6A800] text-white font-head font-700 uppercase text-sm tracking-wider"
            >
              <i className="ri-phone-line" />
              +90 551 183 09 18
            </a>
          </div>
        )}
      </nav>

      {/* WhatsApp FAB */}
      <a
        href="https://wa.me/905511830918"
        target="_blank"
        rel="nofollow noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-[#25D366] text-white cursor-pointer hover:scale-110 transition-transform rounded-full"
        title="WhatsApp ile Yaz"
      >
        <i className="ri-whatsapp-line text-2xl" />
      </a>
    </>
  );
};

export default Navbar;
